import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import DataTable from 'react-data-table-component';

import { perfilEmpleado, limpiarEmpleado } from '../../actions/empleados';
import { editarBreadcrumb } from '../../actions/breadcrumb';
import { formatNumber } from '../../utils/generalFunctions';

const tiposEmpleado = [
    { value: 0, label: 'Cajero' },
    { value: 1, label: 'Bateador' },
    { value: 2, label: 'Promotor' },
    { value: 3, label: 'Freidor' },
    { value: 4, label: 'Vendedor ambulante' },
    { value: 5, label: 'Administrador' }
];

const formatFecha = (fecha) => fecha
    ? new Date(fecha).toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' })
    : '-';

const formatFechaHora = (fecha) => fecha
    ? new Date(fecha).toLocaleString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : '-';

const formatNombre = (persona) => persona
    ? `${persona.nombre || ''} ${persona.apellido_paterno || ''} ${persona.apellido_materno || ''}`.trim()
    : '-';

const PerfilEmpleado = () => {

    const dispatch = useDispatch();
    const { id } = useParams();

    const perfil = useSelector(state => state.empleados.perfil);

    useEffect(() => {
        if (id) {
            dispatch(perfilEmpleado(id));
            dispatch(editarBreadcrumb([
                { nombre: 'Empleados', path: '/empleados', activo: false },
                { nombre: 'Perfil del empleado', path: `/empleados/perfil/${id}`, activo: true }
            ]));
        }

        return () => {
            dispatch(limpiarEmpleado());
        };
    }, [id]);

    const nombreCompleto = perfil
        ? `${perfil.nombre || ''} ${perfil.apellido_paterno || ''} ${perfil.apellido_materno || ''}`.trim()
        : 'Perfil del empleado';

    const tipoEmpleado = tiposEmpleado.find(t => t.value === perfil?.tipo);
    const diasAsistencia = (perfil?.asistencias || []).length;

    /* Columnas */
    const columnsVentas = [
        { name: 'Folio',    sortable: true, wrap: true, selector: row => row.folio || '-' },
        { name: 'Fecha',    sortable: true, wrap: true, selector: row => formatFechaHora(row.fecha) },
        { name: 'Total',    sortable: true, wrap: true, selector: row => row.total != null ? formatNumber(row.total, 2, '$', '') : '-' },
        {
            name: 'Status', sortable: true,
            cell: row => (
                <span className={`badge ${row.status === true ? 'bg-success' : 'bg-danger'}`}>
                    {row.status === true ? 'Realizada' : 'Cancelada'}
                </span>
            )
        },
        { name: 'Sucursal', sortable: true, wrap: true, grow: 1.5, selector: row => row.sucursal?.nombre || '-' },
        {
            name: 'Tipo', sortable: true,
            selector: row => {
                const tipo = tiposEmpleado.find(t => t.value === row.tipo);
                return tipo ? tipo.label : '-';
            }
        }
    ];

    const columnsVueltas = [
        { name: 'Folio',    sortable: true, wrap: true, selector: row => row.folio || '-' },
        { name: 'Fecha',    sortable: true, wrap: true, selector: row => formatFechaHora(row.fecha) },
        { name: 'Total',    sortable: true, wrap: true, selector: row => row.total != null ? formatNumber(row.total, 2, '$', '') : '-' },
        {
            name: 'Status', sortable: true,
            cell: row => (
                <span className={`badge ${row.status === true ? 'bg-success' : 'bg-danger'}`}>
                    {row.status === true ? 'Realizada' : 'Cancelada'}
                </span>
            )
        },
        { name: 'Sucursal', sortable: true, wrap: true, grow: 1.5, selector: row => row.sucursal?.nombre || '-' },
        {
            name: 'Tipo', sortable: true,
            selector: row => {
                const tipo = tiposEmpleado.find(t => t.value === row.tipo);
                return tipo ? tipo.label : '-';
            }
        }
    ];

    const columnsAsistencias = [
        { name: 'Fecha',        sortable: true, wrap: true,            selector: row => formatFecha(row.fecha) },
        { name: 'Responsable',  sortable: true, wrap: true, grow: 2,   selector: row => formatNombre(row.responsable) },
        { name: 'Sucursal',     sortable: true, wrap: true, grow: 2,   selector: row => row.sucursal?.nombre  || 'Sin definir' },
        { name: 'Evento',       sortable: true, wrap: true, grow: 2,   selector: row => row.evento?.nombre    || 'Sin definir' }
    ];

    const columnsCortes = [
        { name: 'Folio',             sortable: true, wrap: true,            selector: row => row.folio || '-' },
        { name: 'Fecha',             sortable: true, wrap: true,            selector: row => formatFechaHora(row.fecha) },
        { name: 'Total inicial',     sortable: true, wrap: true,            selector: row => row.total_inicial  != null ? formatNumber(row.total_inicial,  2, '$', '') : '-' },
        { name: 'Total ventas',      sortable: true, wrap: true,            selector: row => row.total_ventas   != null ? formatNumber(row.total_ventas,   2, '$', '') : '-' },
        { name: 'Total ventas amb.', sortable: true, wrap: true,            selector: row => row.total_vueltas  != null ? formatNumber(row.total_vueltas,  2, '$', '') : '-' },
        { name: 'Total gastos',      sortable: true, wrap: true,            selector: row => row.total_gastos   != null ? formatNumber(row.total_gastos,   2, '$', '') : '-' },
        {
            name: 'Status', sortable: true,
            cell: row => (
                <span className={`badge ${row.status === true ? 'bg-success' : 'bg-warning text-dark'}`}>
                    {row.status === true ? 'Finalizado' : 'Pendiente'}
                </span>
            )
        },
        { name: 'Sucursal', sortable: true, wrap: true, grow: 1.5, selector: row => row.sucursal?.nombre || '-' }
    ];

    const columnsNominas = [
        { name: 'Período',      sortable: true, wrap: true, grow: 2,   selector: row => `${formatFecha(row.fecha_inicio)} – ${formatFecha(row.fecha_fin)}` },
        { name: 'Responsable',  sortable: true, wrap: true, grow: 1.5, selector: row => formatNombre(row.responsable) },
        { name: 'Sueldo',       sortable: true, wrap: true,            selector: row => row.sueldo      != null ? formatNumber(row.sueldo,      2, '$', '') : '-' },
        { name: 'Comisiones',   sortable: true, wrap: true,            selector: row => row.comisiones  != null ? formatNumber(row.comisiones,  2, '$', '') : '-' },
        { name: 'Bonos',        sortable: true, wrap: true,            selector: row => row.bonos       != null ? formatNumber(row.bonos,       2, '$', '') : '-' },
        { name: 'Préstamos',    sortable: true, wrap: true,            selector: row => row.prestamos   != null ? formatNumber(row.prestamos,   2, '$', '') : '-' },
        { name: 'Descuentos',   sortable: true, wrap: true,            selector: row => row.descuentos  != null ? formatNumber(row.descuentos,  2, '$', '') : '-' },
        { name: 'Total',        sortable: true, wrap: true,            selector: row => row.total       != null ? formatNumber(row.total,       2, '$', '') : '-' }
    ];

    const noDataComponent = (icono, texto) => (
        <div className="py-5 text-center text-muted">
            <i className={`fas ${icono} fa-2x mb-2 d-block opacity-25`}></i>
            {texto}
        </div>
    );

    const dataTableProps = {
        pagination: true,
        paginationPerPage: 20,
        paginationComponentOptions: { rowsPerPageText: 'Filas por página', rangeSeparatorText: 'de' },
        highlightOnHover: true
    };

    return (
        <div className="row mt-5">

            {/* Header */}
            <div className="col-12 mb-4">
                <div className="d-flex align-items-center gap-3">
                    <div className="rounded-circle bg-secondary bg-opacity-10 d-flex align-items-center justify-content-center" style={{ width: 48, height: 48, minWidth: 48 }}>
                        <i className="fas fa-user text-primary fs-5"></i>
                    </div>
                    <div>
                        <h4 className="mb-0 fw-semibold">{nombreCompleto}</h4>
                        <small className="text-muted">
                            {(perfil?.sucursales || []).map(s => s.nombre || s).filter(Boolean).join(', ') || 'Sin sucursal asignada'}
                        </small>
                    </div>
                    <div className="ms-auto">
                        {tipoEmpleado && (
                            <span className="badge fs-6 px-3 py-2 bg-success">
                                <i className="fas fa-id-badge me-1"></i>
                                {tipoEmpleado.label}
                            </span>
                        )}
                    </div>
                </div>
            </div>

            <div className="col-md mb-3">
                <div className="card border-0 shadow-sm h-100">
                    <div className="card-body d-flex align-items-center gap-3">
                        <div className="rounded-circle bg-success bg-opacity-10 d-flex align-items-center justify-content-center" style={{ width: 44, height: 44, minWidth: 44 }}>
                            <i className="fas fa-shopping-cart text-success"></i>
                        </div>
                        <div>
                            <div className="text-muted small">Total ventas</div>
                            <div className="fw-bold fs-5">{perfil?.ventas?.length || 0}</div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="col-md mb-3">
                <div className="card border-0 shadow-sm h-100">
                    <div className="card-body d-flex align-items-center gap-3">
                        <div className="rounded-circle bg-success bg-opacity-10 d-flex align-items-center justify-content-center" style={{ width: 44, height: 44, minWidth: 44 }}>
                            <i className="fas fa-redo text-success"></i>
                        </div>
                        <div>
                            <div className="text-muted small">Total ventas ambulantes</div>
                            <div className="fw-bold fs-5">{perfil?.vueltas?.length || 0}</div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="col-md mb-3">
                <div className="card border-0 shadow-sm h-100">
                    <div className="card-body d-flex align-items-center gap-3">
                        <div className="rounded-circle bg-info bg-opacity-10 d-flex align-items-center justify-content-center" style={{ width: 44, height: 44, minWidth: 44 }}>
                            <i className="fas fa-ice-cream text-info"></i>
                        </div>
                        <div>
                            <div className="text-muted small">Total conos</div>
                            <div className="fw-bold fs-5">{perfil?.total_conos ?? '-'}</div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="col-md mb-3">
                <div className="card border-0 shadow-sm h-100">
                    <div className="card-body d-flex align-items-center gap-3">
                        <div className="rounded-circle bg-secondary bg-opacity-10 d-flex align-items-center justify-content-center" style={{ width: 44, height: 44, minWidth: 44 }}>
                            <i className="fas fa-clipboard-check text-primary"></i>
                        </div>
                        <div>
                            <div className="text-muted small">Días con asistencia</div>
                            <div className="fw-bold fs-5">{diasAsistencia}</div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="col-md mb-3">
                <div className="card border-0 shadow-sm h-100">
                    <div className="card-body d-flex align-items-center gap-3">
                        <div className="rounded-circle bg-warning bg-opacity-10 d-flex align-items-center justify-content-center" style={{ width: 44, height: 44, minWidth: 44 }}>
                            <i className="fas fa-dollar-sign text-warning"></i>
                        </div>
                        <div>
                            <div className="text-muted small">Sueldo diario</div>
                            <div className="fw-bold fs-5">{perfil?.sueldo != null ? formatNumber(perfil.sueldo, 2, '$', '') : '-'}</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Acordeón */}
            <div className="col-12">
                <div className="accordion" id="accordionPerfilEmpleado">

                    {/* Sección 1: Información general */}
                    <div className="accordion-item border-0 shadow-sm mb-2">
                        <h2 className="accordion-header" id="peHeadingInfo">
                            <button className="accordion-button" type="button" data-bs-toggle="collapse" data-bs-target="#peCollapseInfo" aria-expanded="true" aria-controls="peCollapseInfo">
                                <i className="fas fa-info-circle text-primary me-2"></i>
                                Información del empleado
                            </button>
                        </h2>
                        <div id="peCollapseInfo" className="accordion-collapse collapse show" aria-labelledby="peHeadingInfo" data-bs-parent="#accordionPerfilEmpleado">
                            <div className="accordion-body">
                                <div className="row g-3">
                                    <div className="col-md-6">
                                        <div className="border-start border-primary border-3 ps-3">
                                            <div className="text-muted small mb-1">Nombre completo</div>
                                            <div className="fw-semibold">{nombreCompleto}</div>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="border-start border-primary border-3 ps-3">
                                            <div className="text-muted small mb-1">Tipo</div>
                                            <div className="fw-semibold">{tipoEmpleado?.label || '-'}</div>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="border-start border-secondary border-3 ps-3">
                                            <div className="text-muted small mb-1">Teléfono</div>
                                            <div className="fw-semibold">{perfil?.telefono || '-'}</div>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="border-start border-secondary border-3 ps-3">
                                            <div className="text-muted small mb-1">Usuario (PDV)</div>
                                            <div className="fw-semibold">{perfil?.usuario || '-'}</div>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="border-start border-warning border-3 ps-3">
                                            <div className="text-muted small mb-1">Sucursales asignadas</div>
                                            <div className="fw-semibold">
                                                {(perfil?.sucursales || []).map(s => s.nombre || s).filter(Boolean).join(', ') || '-'}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="border-start border-warning border-3 ps-3">
                                            <div className="text-muted small mb-1">Sueldo diario</div>
                                            <div className="fw-semibold">{perfil?.sueldo != null ? formatNumber(perfil.sueldo, 2, '$', '') : '-'}</div>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="border-start border-success border-3 ps-3">
                                            <div className="text-muted small mb-1">Cortesía (100%)</div>
                                            <div className="fw-semibold">{perfil?.cortesia === true ? 'Sí' : perfil?.cortesia === false ? 'No' : '-'}</div>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="border-start border-success border-3 ps-3">
                                            <div className="text-muted small mb-1">Precio especial (50%)</div>
                                            <div className="fw-semibold">{perfil?.especial === true ? 'Sí' : perfil?.especial === false ? 'No' : '-'}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Sección 2: Ventas */}
                    <div className="accordion-item border-0 shadow-sm mb-2">
                        <h2 className="accordion-header" id="peHeadingVentas">
                            <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#peCollapseVentas" aria-expanded="false" aria-controls="peCollapseVentas">
                                <i className="fas fa-shopping-cart text-success me-2"></i>
                                Ventas
                                {perfil?.ventas?.length > 0 && (
                                    <span className="badge bg-success rounded-pill ms-2">{perfil.ventas.length}</span>
                                )}
                            </button>
                        </h2>
                        <div id="peCollapseVentas" className="accordion-collapse collapse" aria-labelledby="peHeadingVentas" data-bs-parent="#accordionPerfilEmpleado">
                            <div className="accordion-body p-0">
                                <DataTable
                                    columns={columnsVentas}
                                    data={perfil?.ventas || []}
                                    {...dataTableProps}
                                    noDataComponent={noDataComponent('fa-shopping-cart', 'No hay ventas registradas')}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Sección 3: Ventas ambulantes */}
                    <div className="accordion-item border-0 shadow-sm mb-2">
                        <h2 className="accordion-header" id="peHeadingVueltas">
                            <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#peCollapseVueltas" aria-expanded="false" aria-controls="peCollapseVueltas">
                                <i className="fas fa-redo text-success me-2"></i>
                                Ventas ambulantes
                                {perfil?.vueltas?.length > 0 && (
                                    <span className="badge bg-success rounded-pill ms-2">{perfil.vueltas.length}</span>
                                )}
                            </button>
                        </h2>
                        <div id="peCollapseVueltas" className="accordion-collapse collapse" aria-labelledby="peHeadingVueltas" data-bs-parent="#accordionPerfilEmpleado">
                            <div className="accordion-body p-0">
                                <DataTable
                                    columns={columnsVueltas}
                                    data={perfil?.vueltas || []}
                                    {...dataTableProps}
                                    noDataComponent={noDataComponent('fa-redo', 'No hay ventas ambulantes registradas')}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Sección 4: Asistencias */}
                    <div className="accordion-item border-0 shadow-sm mb-2">
                        <h2 className="accordion-header" id="peHeadingAsistencias">
                            <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#peCollapseAsistencias" aria-expanded="false" aria-controls="peCollapseAsistencias">
                                <i className="fas fa-clipboard-check text-primary me-2"></i>
                                Asistencias
                                {perfil?.asistencias?.length > 0 && (
                                    <span className="badge bg-secondary rounded-pill ms-2">{perfil.asistencias.length}</span>
                                )}
                            </button>
                        </h2>
                        <div id="peCollapseAsistencias" className="accordion-collapse collapse" aria-labelledby="peHeadingAsistencias" data-bs-parent="#accordionPerfilEmpleado">
                            <div className="accordion-body p-0">
                                <DataTable
                                    columns={columnsAsistencias}
                                    data={perfil?.asistencias || []}
                                    {...dataTableProps}
                                    noDataComponent={noDataComponent('fa-clipboard-check', 'No hay asistencias registradas')}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Sección 5: Cortes de caja */}
                    <div className="accordion-item border-0 shadow-sm mb-2">
                        <h2 className="accordion-header" id="peHeadingCortes">
                            <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#peCollapseCortes" aria-expanded="false" aria-controls="peCollapseCortes">
                                <i className="fas fa-cash-register text-danger me-2"></i>
                                Cortes de caja
                                {perfil?.cortes?.length > 0 && (
                                    <span className="badge bg-danger rounded-pill ms-2">{perfil.cortes.length}</span>
                                )}
                            </button>
                        </h2>
                        <div id="peCollapseCortes" className="accordion-collapse collapse" aria-labelledby="peHeadingCortes" data-bs-parent="#accordionPerfilEmpleado">
                            <div className="accordion-body p-0">
                                <DataTable
                                    columns={columnsCortes}
                                    data={perfil?.cortes || []}
                                    {...dataTableProps}
                                    noDataComponent={noDataComponent('fa-cash-register', 'No hay cortes de caja registrados')}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Sección 6: Nóminas */}
                    <div className="accordion-item border-0 shadow-sm mb-2">
                        <h2 className="accordion-header" id="peHeadingNominas">
                            <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#peCollapseNominas" aria-expanded="false" aria-controls="peCollapseNominas">
                                <i className="fas fa-money-check-alt text-warning me-2"></i>
                                Nóminas
                                {perfil?.nominas?.length > 0 && (
                                    <span className="badge bg-warning text-dark rounded-pill ms-2">{perfil.nominas.length}</span>
                                )}
                            </button>
                        </h2>
                        <div id="peCollapseNominas" className="accordion-collapse collapse" aria-labelledby="peHeadingNominas" data-bs-parent="#accordionPerfilEmpleado">
                            <div className="accordion-body p-0">
                                <DataTable
                                    columns={columnsNominas}
                                    data={perfil?.nominas || []}
                                    {...dataTableProps}
                                    noDataComponent={noDataComponent('fa-money-check-alt', 'No hay nóminas registradas')}
                                />
                            </div>
                        </div>
                    </div>

                </div>
            </div>

        </div>
    );
};

export default PerfilEmpleado;
